import { createContext, useContext, useState, useEffect } from 'react';
import { authApi, saveTokens, clearTokens, getTokens } from '../api/api';

const AuthContext = createContext(null);

const SESSION_KEY = 'bhu_portal_session';

/* The server's role strings collapsed to the three the UI routes on. Night
   staff get a role of their own because their whole session is a single screen
   — see pages/NightDeskPage.js. */
const ROLE_FROM_API = {
  SUPER_ADMIN: 'admin',
  GH_ADMIN:    'admin',
  NIGHT_STAFF: 'night',
};

/* Where a session lands. App's two redirects, LoginPage and ProtectedRoute's
   fallback each used to spell this out for themselves, which is how a fourth
   role gets missed. `userFallback` stays a parameter because the call sites
   genuinely disagree about where a regular user belongs: fresh off the login
   form that's the booking form, everywhere else it's their bookings list. */
export const homePathFor = (role, userFallback = '/my-bookings') => (
  role === 'admin' ? '/gh-admin' : role === 'night' ? '/night' : userFallback
);

/* Always land admins on the dashboard summary (home) after login, regardless
   of whatever tab/view they had open the last time they were signed in. */
const resetAdminView = () => {
  localStorage.setItem('admin_active_tab', 'summary');
  localStorage.setItem('admin_show_calendar', 'false');
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) { try { return JSON.parse(stored); } catch { /* ignore */ } }
    return null;
  });

  /* Token refresh failed (api.js) — the session is unrecoverable, so drop the
     logged-in user immediately instead of leaving stale/cached data on screen. */
  useEffect(() => {
    const onSessionExpired = () => {
      setCurrentUser(null);
      localStorage.removeItem(SESSION_KEY);
    };
    window.addEventListener('bhu:session-expired', onSessionExpired);
    return () => window.removeEventListener('bhu:session-expired', onSessionExpired);
  }, []);

  /* ── Login: tries real API first, falls back to local seed ── */
  const login = async (username, password) => {
    /* Try JWT API */
    try {
      const data = await authApi.login(username, password);
      saveTokens(data.access, data.refresh);
      const role    = ROLE_FROM_API[data.role] || 'user';
      const session = {
        id:       String(data.id || username),
        fullName: data.name || username,
        email:    username,
        username,
        role,
        apiRole:  data.role,
        // Per-field inline indent editing. A pre-existing bhu_portal_session
        // blob from before this key existed reads falsy here until the next
        // login — acceptable, it just means no pencils until then.
        hasEditAccess: Boolean(data.has_edit_access),
        // Billing (and later) capabilities; see utils/capabilities.js.
        capabilities: Array.isArray(data.capabilities) ? data.capabilities : undefined,
      };
      setCurrentUser(session);
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      if (role === 'admin') resetAdminView();
      window.dispatchEvent(new Event('bhu:logged-in'));
      return { ok: true, role };
    } catch (apiErr) {
      /* Fallback: check hardcoded seed credentials for dev/demo */
      const SEED_USERS = [
        { id: 'admin-001', fullName: 'Rahul Singh',  email: 'admin@bhu.ac.in',        username: 'admin',        role: 'admin', password: 'admin123' },
        { id: 'user-001',  fullName: 'Priya Sharma', email: 'priya.sharma@bhu.ac.in', username: 'priya.sharma', role: 'user',  password: 'user123'  },
        { id: 'user-002',  fullName: 'Amit Singh',   email: 'amit.singh@bhu.ac.in',   username: 'amit.singh',   role: 'user',  password: 'user123'  },
      ];
      const stored = localStorage.getItem('bhu_portal_users');
      const users  = stored ? JSON.parse(stored) : SEED_USERS;
      /* match by username OR email (backward compat) */
      const user = users.find(u =>
        (u.username === username || u.email === username) && u.password === password
      );
      if (!user) {
        const isNetworkError = apiErr instanceof TypeError || (apiErr.message && apiErr.message.toLowerCase().includes('fetch'));
        const error = isNetworkError ? 'Invalid username or password.' : (apiErr.message || 'Invalid username or password.');
        return { ok: false, error };
      }
      const session = { id: user.id, fullName: user.fullName, email: user.email, username: user.username || username, role: user.role, hasEditAccess: false };
      setCurrentUser(session);
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      if (user.role === 'admin') resetAdminView();
      return { ok: true, role: user.role };
    }
  };

  /* ── Logout ─────────────────────────────────────────────── */
  const logout = async () => {
    const { refresh } = getTokens();
    if (refresh) {
      try { await authApi.logout(refresh); } catch { /* best-effort */ }
    }
    clearTokens();
    setCurrentUser(null);
    localStorage.removeItem(SESSION_KEY);
    window.dispatchEvent(new Event('bhu:logged-out'));
  };

  /* ── Register (local only — no API endpoint) ─────────────── */
  const register = ({ fullName, email, phone, password }) => {
    const USERS_KEY = 'bhu_portal_users';
    const SEED_USERS = [
      { id: 'admin-001', fullName: 'Rahul Singh',  email: 'admin@bhu.ac.in',        role: 'admin', password: 'admin123' },
      { id: 'user-001',  fullName: 'Priya Sharma', email: 'priya.sharma@bhu.ac.in', role: 'user',  password: 'user123'  },
      { id: 'user-002',  fullName: 'Amit Singh',   email: 'amit.singh@bhu.ac.in',   role: 'user',  password: 'user123'  },
    ];
    const stored = localStorage.getItem(USERS_KEY);
    const users  = stored ? JSON.parse(stored) : SEED_USERS;
    if (users.find(u => u.email === email)) {
      return { ok: false, error: 'This email is already registered.' };
    }
    const newUser = { id: `user-${Date.now()}`, fullName, email, phone, role: 'user', password };
    const updated = [...users, newUser];
    localStorage.setItem(USERS_KEY, JSON.stringify(updated));
    return { ok: true };
  };

  return (
    <AuthContext.Provider value={{ currentUser, login, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
